import { ElementType, ForwardedRef, forwardRef, JSX } from 'react';
import DOMPurify from 'isomorphic-dompurify';

// List of html tags (excluding web-components)
type validTags = keyof JSX.IntrinsicElements & keyof HTMLElementTagNameMap;

// Dynamic tag props
type PurifiedHtmlProps<Tag extends validTags = 'div'> = {
  htmlString: string;
  forceBody?: boolean;
  allowScript?: boolean;
  as?: Tag;
} & JSX.IntrinsicElements[Tag];

export const PurifiedHtml = forwardRef(
  <
    Tag extends validTags = 'div',
    Element extends HTMLElement = HTMLElementTagNameMap[Tag]
  >(
    {
      htmlString = '',
      as: tag,
      /** Allows elements like style, script or others to be injected. For more info see https://github.com/cure53/DOMPurify */
      forceBody = false,
      allowScript = false,
      ...props
    }: PurifiedHtmlProps<Tag>,
    // Type ref with the HTML Element according with the tag passed
    ref: ForwardedRef<Element>
  ) => {
    const Component = (tag || 'div') as ElementType;
    return (
      <Component
        {...props}
        ref={ref}
        dangerouslySetInnerHTML={{
          // DOMPurify removes iframe tags when placing the html so in order to prevent that we have added ADD_TAGS: ["iframe"]
          // which will allow iframe tags
          // which will allow target attributes
          __html: DOMPurify.sanitize(htmlString, {
            ADD_TAGS: ['iframe', allowScript ? 'script' : ''],
            ADD_ATTR: ['target'],
            FORCE_BODY: forceBody,
            CUSTOM_ELEMENT_HANDLING: {
              tagNameCheck: /^cat-/, // allow all tags starting with "cat-"
              attributeNameCheck: /^/,
              allowCustomizedBuiltInElements: true, // customized built-ins are allowed
            },
          }),
        }}
      />
    );
  }
);

PurifiedHtml.displayName = 'PurifiedHtml';
